import type {
  ConversationListContactStateName,
  ConversationListSortName,
  ConversationListStatusName,
  PlatformTypeName,
} from "@multi-chat/shared";
import { DEFAULT_CONVERSATION_LIST_SORT, DEFAULT_CONVERSATION_LIST_STATUS } from "@multi-chat/shared";
import type { ConversationListQueryState } from "./conversation-list-state.js";

export type PlatformFilterValue = PlatformTypeName | undefined;
export type ContactStateFilterValue = ConversationListContactStateName | undefined;

/** Desktop secondary filter badge — counts unread + contactState only. */
export type ListStatusFilterValue = ConversationListStatusName;

export function activeSecondaryFilterCount(input: {
  unread?: boolean;
  contactState?: ContactStateFilterValue;
  status?: ListStatusFilterValue;
}): number {
  let count = 0;
  if (input.unread === true) count += 1;
  if (input.contactState != null) count += 1;
  if (input.status != null && input.status !== DEFAULT_CONVERSATION_LIST_STATUS) count += 1;
  return count;
}

/**
 * Mobile active-filter count — counts ALL non-default controls:
 * platform, unread, contactState, sort.  Search is excluded.
 */
export function mobileActiveFilterCount(input: {
  platform?: PlatformFilterValue;
  unread?: boolean;
  contactState?: ContactStateFilterValue;
  sort?: ConversationListSortName;
  status?: ListStatusFilterValue;
}): number {
  let count = 0;
  if (input.platform != null) count += 1;
  if (input.unread === true) count += 1;
  if (input.contactState != null) count += 1;
  if (input.status != null && input.status !== DEFAULT_CONVERSATION_LIST_STATUS) count += 1;
  if (input.sort != null && input.sort !== DEFAULT_CONVERSATION_LIST_SORT) count += 1;
  return count;
}

/**
 * Filter button badge next to the quick toggles: unread and sort have their own pressed
 * toggles, so only channel and contact count here.
 */
export function listFilterButtonCount(input: {
  platform?: PlatformFilterValue;
  contactState?: ContactStateFilterValue;
  status?: ListStatusFilterValue;
}): number {
  let count = 0;
  if (input.platform != null) count += 1;
  if (input.contactState != null) count += 1;
  if (input.status != null && input.status !== DEFAULT_CONVERSATION_LIST_STATUS) count += 1;
  return count;
}

export type ListFilterChip =
  | { key: "search"; query: string }
  | { key: "platform"; platform: PlatformTypeName }
  | { key: "contact"; contactState: ConversationListContactStateName }
  | { key: "status"; status: Exclude<ConversationListStatusName, "ALL"> };

/** Removable chips for active list constraints. Search shows only while its field is closed. */
export function listFilterChips(input: {
  search?: string;
  searchOpen: boolean;
  platform?: PlatformFilterValue;
  contactState?: ContactStateFilterValue;
  status?: ListStatusFilterValue;
}): ListFilterChip[] {
  const chips: ListFilterChip[] = [];
  const query = input.search?.trim();
  if (query && !input.searchOpen) chips.push({ key: "search", query });
  if (input.platform != null) chips.push({ key: "platform", platform: input.platform });
  if (input.contactState != null) chips.push({ key: "contact", contactState: input.contactState });
  if (input.status != null && input.status !== "ALL") chips.push({ key: "status", status: input.status });
  return chips;
}

/** Reset appears once there is more than one chip or any popover filter is active. */
export function showListFilterReset(chipCount: number, filterButtonCount: number): boolean {
  return chipCount > 1 || filterButtonCount > 0;
}

export function clearSecondaryFilters(
  query: ConversationListQueryState,
): ConversationListQueryState {
  return {
    search: query.search,
    platform: query.platform,
    sort: query.sort,
  };
}

export function hasAnyListFilters(query: ConversationListQueryState): boolean {
  return (
    query.platform != null ||
    query.unread === true ||
    query.contactState != null ||
    (query.status != null && query.status !== "ALL")
  );
}

export interface MobileFilterSortDraft {
  platform: PlatformFilterValue;
  unread: boolean;
  contactState: ContactStateFilterValue;
  status: ConversationListStatusName;
  sort: ConversationListSortName;
}

export function defaultMobileFilterSortDraft(): MobileFilterSortDraft {
  return {
    platform: undefined,
    unread: false,
    contactState: undefined,
    status: DEFAULT_CONVERSATION_LIST_STATUS,
    sort: DEFAULT_CONVERSATION_LIST_SORT,
  };
}

export function mobileFilterSortDraftFromQuery(query: {
  platform?: PlatformFilterValue;
  unread: boolean;
  contactState?: ContactStateFilterValue;
  status?: ConversationListStatusName;
  sort: ConversationListSortName;
}): MobileFilterSortDraft {
  return {
    platform: query.platform,
    unread: query.unread,
    contactState: query.contactState,
    status: query.status ?? DEFAULT_CONVERSATION_LIST_STATUS,
    sort: query.sort,
  };
}
